/**
 * Quick tournament create + lifecycle helpers.
 * Create: name + start/end + ≥1 event + optional venue.
 * Format is chosen per event after registration closes (Arena-style).
 */

import {
  COMPETITION_KINDS,
  COMPETITION_STATUSES,
  COMPETITION_VISIBILITY,
  defaultCompetitionSettings,
  emptyCompetitionDates,
  emptyCompetitionVenue,
} from "./competitionTypes";
import { createManagedEventSummary } from "../event/eventTypes";
import {
  normalizeEventDefinition,
  validateEventDefinition,
} from "../event/eventCompetition";
import { legacyEventDefinitionFromDisplayName } from "../event/eventCompetitionLegacy";
import { QUICK_EVENT_CHIP_LABELS } from "../event/quickEventPresets";
import { getDefaultMatchFormatForSport } from "../sport/sportMatchFormatCatalog";
import { getDefaultEventMatchFormatForSport } from "../event/eventMatchFormat";
import { normalizeSportId } from "../sport/sportTypes";

/** Arena-style format intents (post-registration, per event). */
export const ARENA_FORMAT_OPTIONS = Object.freeze([
  {
    id: "round-robin",
    title: "Round Robin",
    desc: "Every entry plays every other entry.",
  },
  {
    id: "qualifier",
    title: "Group Stage",
    desc: "Groups first; top finishers advance to knockout.",
  },
  {
    id: "knockout",
    title: "Direct Knockout",
    desc: "Single-elimination bracket from the entry list.",
  },
]);

/** @deprecated Prefer ARENA_FORMAT_OPTIONS — kept for older tests / labels */
export const TOURNAMENT_FORMAT_OPTIONS = Object.freeze([
  {
    id: "casual",
    title: "Casual",
    desc: "Club night / free play — set structure later",
    kind: COMPETITION_KINDS.CLUB_NIGHT,
  },
  {
    id: "round_robin",
    title: "Round Robin",
    desc: "Everyone plays everyone",
    kind: COMPETITION_KINDS.TOURNAMENT_DRAW,
  },
  {
    id: "knockout",
    title: "Knockout",
    desc: "Single-elimination bracket",
    kind: COMPETITION_KINDS.TOURNAMENT_DRAW,
  },
  {
    id: "league",
    title: "League",
    desc: "Season standings over time",
    kind: COMPETITION_KINDS.LEAGUE,
  },
  {
    id: "team",
    title: "Team Event",
    desc: "Team vs team competition",
    kind: COMPETITION_KINDS.TOURNAMENT_DRAW,
  },
]);

/** @deprecated Prefer QUICK_EVENT_PRESETS — labels for legacy UI/tests */
export const QUICK_EVENT_CHIPS = QUICK_EVENT_CHIP_LABELS;

/**
 * Normalize quick-create events from structured `events` or legacy `eventNames`.
 * @param {object} input
 * @returns {import('../event/eventCompetition').TournamentEventDefinition[]}
 */
export function normalizeQuickCreateEvents(input = {}) {
  if (Array.isArray(input.events) && input.events.length) {
    return input.events
      .map((e) => normalizeEventDefinition(e))
      .filter(Boolean);
  }
  const eventNames = Array.isArray(input.eventNames)
    ? input.eventNames.map((n) => String(n || "").trim()).filter(Boolean)
    : [];
  return eventNames
    .map((name) => legacyEventDefinitionFromDisplayName(name))
    .filter(Boolean);
}

export { QUICK_EVENT_PRESETS, eventDefinitionFromPresetId } from "../event/quickEventPresets";

/**
 * @param {string} formatId
 * @deprecated
 */
export function resolveFormatOption(formatId) {
  return (
    TOURNAMENT_FORMAT_OPTIONS.find((o) => o.id === formatId) ||
    TOURNAMENT_FORMAT_OPTIONS.find((o) => o.id === "casual")
  );
}

export function defaultStartMs(nowMs = Date.now()) {
  const d = new Date(nowMs);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.getTime();
}

export function defaultEndMs(startMs) {
  const d = new Date(startMs);
  d.setHours(23, 59, 0, 0);
  return d.getTime();
}

/**
 * Validate essentials for quick create.
 * @param {object} input
 * @returns {{ ok: boolean, errors: string[], fieldErrors: Record<string, string> }}
 */
export function validateQuickCreateInput(input = {}) {
  const fieldErrors = {};
  const errors = [];
  const name = String(input.name || "").trim();
  if (!name) {
    fieldErrors.name = "Tournament name is required.";
    errors.push(fieldErrors.name);
  }
  const startMs = input.startMs ?? input.dates?.startMs;
  const endMs = input.endMs ?? input.dates?.endMs;
  if (startMs == null || Number.isNaN(Number(startMs))) {
    fieldErrors.startMs = "Tournament start is required.";
    errors.push(fieldErrors.startMs);
  }
  if (endMs == null || Number.isNaN(Number(endMs))) {
    fieldErrors.endMs = "Tournament end is required.";
    errors.push(fieldErrors.endMs);
  }
  if (
    startMs != null &&
    endMs != null &&
    !Number.isNaN(Number(startMs)) &&
    !Number.isNaN(Number(endMs)) &&
    Number(endMs) < Number(startMs)
  ) {
    fieldErrors.endMs = "End must be on or after start.";
    errors.push(fieldErrors.endMs);
  }
  const eventDefs = normalizeQuickCreateEvents(input);
  if (eventDefs.length < 1) {
    fieldErrors.events = "Add at least one event.";
    errors.push(fieldErrors.events);
  } else {
    eventDefs.forEach((def, i) => {
      const v = validateEventDefinition(def);
      if (!v.ok) {
        Object.entries(v.fieldErrors).forEach(([k, msg]) => {
          fieldErrors[`events.${i}.${k}`] = msg;
          errors.push(msg);
        });
      }
    });
  }
  return { ok: errors.length === 0, errors, fieldErrors };
}

/**
 * Build competition create payload — no format on create.
 * @param {{
 *   name: string,
 *   startMs: number,
 *   endMs: number,
 *   eventNames: string[],
 *   venueName?: string,
 *   registrationOpenMs?: number | null,
 *   registrationCloseMs?: number | null,
 *   organizationId?: string | null,
 *   organizerName?: string,
 *   organizerUid?: string,
 *   bannerImageUrl?: string | null,
 *   nowMs?: number,
 * }} input
 */
export function buildQuickCreatePayload(input = {}) {
  const name = String(input.name || "").trim();
  const now = input.nowMs ?? Date.now();
  const startMs = input.startMs != null ? Number(input.startMs) : defaultStartMs(now);
  const endMs = input.endMs != null ? Number(input.endMs) : defaultEndMs(startMs);
  const baseSettings = defaultCompetitionSettings();
  const sportId = normalizeSportId(input.sportId || input.sport);
  const matchFormat = getDefaultEventMatchFormatForSport(sportId);
  const venueName = String(input.venueName || "").trim();
  const eventDefs = normalizeQuickCreateEvents(input);
  const createdBy = input.organizerUid || "organizer";
  const managedEvents = eventDefs.map((def) =>
    createManagedEventSummary({ ...def, createdBy })
  );
  const eventNames = managedEvents.map((e) => e.display?.name || e.name).filter(Boolean);

  const regOpen =
    input.registrationOpenMs != null ? Number(input.registrationOpenMs) : null;
  const regClose =
    input.registrationCloseMs != null ? Number(input.registrationCloseMs) : null;

  return {
    name,
    description: "",
    kind: COMPETITION_KINDS.TOURNAMENT_DRAW,
    sport: input.sportId || input.sport || undefined,
    sportId: input.sportId || input.sport || undefined,
    mode: input.mode || "tournament",
    enabledModules: Array.isArray(input.enabledModules)
      ? input.enabledModules
      : ["tournament"],
    organizationId: input.organizationId || null,
    organizerName: input.organizerName || "Organizer",
    organizerUid: input.organizerUid || undefined,
    bannerImageUrl: input.bannerImageUrl || null,
    logoUrl: null,
    venue: {
      ...emptyCompetitionVenue(),
      ...(venueName ? { name: venueName } : {}),
    },
    dates: {
      ...emptyCompetitionDates(),
      registrationOpenMs: Number.isFinite(regOpen) ? regOpen : null,
      registrationCloseMs: Number.isFinite(regClose) ? regClose : null,
      startMs,
      endMs,
    },
    settings: {
      ...baseSettings,
      visibility: COMPETITION_VISIBILITY.PUBLIC,
      courtCount: 1,
      defaultMatchFormat: {
        setsToWin: matchFormat.setsToWin,
        pointsToWin: matchFormat.pointsToWin,
      },
      registration: {
        allowSelfRegister: true,
        requireApproval: true,
      },
    },
    managedEvents,
    eventId: managedEvents[0]?.eventId || null,
    /** Durable Host Tournament chip names — Event docs may be seeded async. */
    plannedEventNames: eventNames,
    status: COMPETITION_STATUSES.DRAFT,
  };
}

/**
 * Registration is closed for format/generate when status is closed
 * OR registrationCloseMs has passed (organizer can also close early via status).
 * @param {object} competition
 * @param {number} [nowMs]
 */
export function isRegistrationClosedForFormat(competition, nowMs = Date.now()) {
  if (!competition) return false;
  const status = competition.status;
  if (
    status === COMPETITION_STATUSES.REGISTRATION_CLOSED ||
    status === COMPETITION_STATUSES.IN_PROGRESS ||
    status === COMPETITION_STATUSES.COMPLETED
  ) {
    return true;
  }
  const closeMs = competition.dates?.registrationCloseMs;
  if (closeMs != null && Number(closeMs) <= nowMs) {
    return true;
  }
  return false;
}

/**
 * Can organizer still take registrations?
 * @param {object} competition
 * @param {number} [nowMs]
 */
export function isRegistrationAccepting(competition, nowMs = Date.now()) {
  if (!competition) return false;
  if (isRegistrationClosedForFormat(competition, nowMs)) return false;
  const status = competition.status;
  if (
    status === COMPETITION_STATUSES.DRAFT ||
    status === COMPETITION_STATUSES.ARCHIVED ||
    status === COMPETITION_STATUSES.DELETED
  ) {
    return false;
  }
  const openMs = competition.dates?.registrationOpenMs;
  if (openMs != null && Number(openMs) > nowMs) return false;
  return (
    status === COMPETITION_STATUSES.PUBLISHED ||
    status === COMPETITION_STATUSES.REGISTRATION_OPEN ||
    status === COMPETITION_STATUSES.DRAW_PUBLISHED
  );
}

export function competitionFormatLabel(competition) {
  const preferred = competition?.settings?.preferredFormat;
  const arena = ARENA_FORMAT_OPTIONS.find((o) => o.id === preferred);
  if (arena) return arena.title;
  const opt = TOURNAMENT_FORMAT_OPTIONS.find((o) => o.id === preferred);
  if (opt) return opt.title;
  const kind = competition?.kind;
  if (kind === COMPETITION_KINDS.LEAGUE) return "League";
  if (kind === COMPETITION_KINDS.CLUB_NIGHT) return "Casual";
  if (kind === COMPETITION_KINDS.LADDER) return "Ladder";
  return "Tournament";
}

function hasVenue(competition) {
  return Boolean(String(competition?.venue?.name || "").trim());
}

function hasPlayDates(competition) {
  return competition?.dates?.startMs != null && competition?.dates?.endMs != null;
}

function entryCountOf(competition) {
  return (
    competition?.entryCount ??
    (competition?.managedEntries || []).length ??
    (competition?.entries || []).length ??
    0
  );
}

function eventCountOf(competition) {
  return (competition?.managedEvents || []).length;
}

function drawCountOf(competition) {
  return (competition?.managedDraws || []).length;
}

function isPublished(competition) {
  const status = competition?.status;
  return (
    status &&
    status !== COMPETITION_STATUSES.DRAFT &&
    status !== COMPETITION_STATUSES.DELETED
  );
}

/**
 * Lifecycle setup checklist for Tournament Home.
 * @param {object} competition
 * @param {number} [nowMs]
 */
export function projectTournamentSetupChecklist(competition, nowMs = Date.now()) {
  if (!competition) return [];
  const id = competition.competitionId;
  const entries = entryCountOf(competition);
  const events = eventCountOf(competition);
  const courts =
    (competition.managedCourts || []).length ||
    Number(competition.settings?.courtCount) ||
    0;
  const regClosed = isRegistrationClosedForFormat(competition, nowMs);
  const draws = drawCountOf(competition);
  const vis = competition.settings?.visibility || COMPETITION_VISIBILITY.PRIVATE;

  return [
    {
      id: "created",
      label: "Tournament created",
      done: true,
      meta: competition.roomId ? "Arena linked" : "Created",
      path: null,
    },
    {
      id: "events",
      label: "Events",
      done: events > 0,
      meta: events > 0 ? `${events} event${events === 1 ? "" : "s"}` : "Required",
      path: null,
      action: "events",
    },
    {
      id: "dates",
      label: "Dates",
      done: hasPlayDates(competition),
      meta: hasPlayDates(competition)
        ? new Date(competition.dates.startMs).toLocaleString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        : "Required",
      path: `/tournaments/${id}/edit?focus=dates`,
    },
    {
      id: "venue",
      label: "Venue",
      done: hasVenue(competition),
      meta: hasVenue(competition)
        ? String(competition.venue.name).trim()
        : "Not set",
      path: `/tournaments/${id}/edit?focus=venue`,
    },
    {
      id: "players",
      label: "Players",
      done: entries > 0,
      meta: entries > 0 ? `${entries} entr${entries === 1 ? "y" : "ies"}` : "Registration",
      path: `/tournaments/${id}/entries`,
    },
    {
      id: "courts",
      label: "Courts & refs",
      done: courts >= 1,
      meta: courts >= 1 ? `${courts} court${courts === 1 ? "" : "s"}` : "Add courts",
      path: `/tournaments/${id}/courts`,
    },
    {
      id: "registration",
      label: "Close registration",
      done: regClosed,
      meta: regClosed ? "Closed — format unlocked" : "Open — close to set formats",
      path: null,
      action: "closeRegistration",
    },
    {
      id: "format",
      label: "Format & fixtures",
      done: draws > 0,
      meta: !regClosed
        ? "Unlocks after registration closes"
        : draws > 0
          ? `${draws} draw${draws === 1 ? "" : "s"}`
          : "Set format per event",
      path: regClosed ? `/tournaments/${id}/draws` : null,
      locked: !regClosed,
    },
    {
      id: "publish",
      label: "Publish",
      done: isPublished(competition),
      meta: isPublished(competition)
        ? `${vis === COMPETITION_VISIBILITY.PRIVATE ? "Private" : "Public"}`
        : "Still draft",
      path: null,
      action: "publish",
    },
  ];
}

/**
 * Map Arena format UI id → draw engine format + optional qualifyTopN.
 * Qualifier: `topN` from the UI is total advancing; stored `qualifyTopN` is per group.
 * @param {'round-robin'|'qualifier'|'knockout'|string} arenaFormatId
 * @param {{ topN?: number, numGroups?: number }} [opts]
 */
export function mapArenaFormatToDrawFormat(arenaFormatId, opts = {}) {
  if (arenaFormatId === "knockout") {
    return { format: "single_elimination", qualifyTopN: 0, numGroups: 0 };
  }
  const topN = Number(opts.topN);
  const numGroupsRaw = Math.floor(Number(opts.numGroups) || 0);

  if (arenaFormatId === "qualifier") {
    const numGroups = Math.max(2, numGroupsRaw || 2);
    const total = Number.isFinite(topN) ? topN : 0;
    const qualifyTopN =
      total >= numGroups && total % numGroups === 0
        ? total / numGroups
        : Math.max(1, Math.floor(total / numGroups) || 1);
    return {
      format: "round_robin",
      qualifyTopN,
      numGroups,
      stageKind: "group_stage",
    };
  }

  return {
    format: "round_robin",
    qualifyTopN: Number.isFinite(topN) ? topN : 0,
    numGroups: 0,
    stageKind: null,
  };
}
